import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cleanTrackingParams } from '../lib/urlUtils';
import { encryptSecret, generatePassword } from '../lib/vaultCrypto';
import { inferSmartTags } from '../lib/tagClassifier';
import { matchUrlRules } from '../lib/regexRouter';
import { formatCredentialRecord } from '../lib/credentialParser';

const {
  FiX, FiSave, FiLink, FiType, FiFileText,
  FiLock, FiEye, FiEyeOff, FiKey, FiShield, FiCheck, FiUser, FiZap
} = FiIcons;

const EMPTY = { title: '', url: '', note: '', credentials: '', logoUrl: '' };

export default function AddBookmarkModal({
  isOpen,
  onClose,
  onSave,
  themeColors: tc,
  userId = 'default_user',
  isDark: isDarkProp,
}) {
  const [form, setForm] = useState(EMPTY);
  const [credUser, setCredUser] = useState('');
  const [credPass, setCredPass] = useState('');
  const [credCode, setCredCode] = useState('');
  const [showCreds, setShowCreds] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [fetchingLogo, setFetchingLogo] = useState(false);
  const prevOpen = useRef(false);

  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !tc?.card?.toLowerCase().includes('ffffff') && !tc?.card?.startsWith('rgba(255');
  const inputBg = isDark ? 'rgba(0,0,0,0.35)' : '#FFFFFF';
  const inputBorder = isDark ? tc.border : '#CBD5E1';

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setForm(EMPTY);
      setCredUser('');
      setCredPass('');
      setCredCode('');
      setActiveTab('basic');
      setShowCreds(false);
    }
    prevOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!form.url) return;
    try {
      const domain = new URL(form.url).hostname;
      setFetchingLogo(true);
      setForm((p) => {
        const update = { ...p, logoUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=128` };
        const routing = matchUrlRules(form.url);
        if (routing.matched && routing.addedTags.length > 0) {
          const curNote = p.note ? p.note.trim() : '';
          const newTags = routing.addedTags.filter((t) => !curNote.includes(t)).join(' ');
          if (newTags) {
            update.note = curNote ? `${curNote} ${newTags}` : newTags;
          }
        }
        return update;
      });
    } catch (_) {}
    finally { setFetchingLogo(false); }
  }, [form.url]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const inputStyle = { backgroundColor: inputBg, borderColor: inputBorder, color: tc.text };
  const onFocus = (e) => (e.currentTarget.style.borderColor = tc.accent);
  const onBlur = (e) => (e.currentTarget.style.borderColor = inputBorder);

  const tabs = [
    { id: 'basic', label: 'Basic' },
    { id: 'note', label: 'Note' },
    { id: 'credentials', label: 'Credentials' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="border rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto overflow-hidden"
              style={{ backgroundColor: tc.card, borderColor: tc.border }}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${tc.accent}22` }}>
                    <SafeIcon icon={FiLink} className="w-4 h-4" style={{ color: tc.accent }} />
                  </div>
                  <h2 className="text-lg font-semibold" style={{ color: tc.text }}>Add Bookmark</h2>
                </div>
                <button onClick={onClose} style={{ color: tc.muted }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = tc.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = tc.muted)}>
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 px-6 mb-4">
                {tabs.map((t) => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={activeTab === t.id
                      ? { backgroundColor: `${tc.accent}22`, color: tc.accent, border: `1px solid ${tc.accent}55` }
                      : { color: tc.muted, border: '1px solid transparent' }}>
                    {t.label}
                  </button>
                ))}
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                const cleanedUrl = cleanTrackingParams(form.url.trim());
                const formatted = formatCredentialRecord({
                  username: credUser.trim(),
                  password: credPass.trim(),
                  activationCode: credCode.trim()
                });
                let finalCreds = formatted || form.credentials.trim();
                if (finalCreds) {
                  finalCreds = await encryptSecret(finalCreds, userId);
                }
                onSave({ ...form, url: cleanedUrl, credentials: finalCreds, type: 'link' });
              }}>
                <div className="px-6 pb-2 space-y-4" style={{ minHeight: 200 }}>
                  <AnimatePresence mode="wait">

                    {activeTab === 'basic' && (
                      <motion.div key="basic" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: tc.muted }}>Title</label>
                          <div className="relative">
                            <SafeIcon icon={FiType} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: tc.muted }} />
                            <input type="text" value={form.title} onChange={set('title')} required
                              placeholder="Enter title..." onFocus={onFocus} onBlur={onBlur}
                              className="w-full border rounded-lg px-10 py-2.5 text-sm focus:outline-none transition-colors"
                              style={inputStyle} />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-medium" style={{ color: tc.muted }}>URL</label>
                            {form.url && form.url.includes('?') && (
                              <button
                                type="button"
                                onClick={() => setForm((p) => ({ ...p, url: cleanTrackingParams(p.url) }))}
                                className="text-[10px] text-emerald-400 hover:underline flex items-center gap-1"
                                title="Strip UTM and tracking parameters"
                              >
                                <SafeIcon icon={FiShield} className="w-2.5 h-2.5" /> Strip Trackers
                              </button>
                            )}
                          </div>
                          <div className="relative">
                            <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: tc.muted }} />
                            <input type="url" value={form.url} onChange={set('url')} required
                              placeholder="https://example.com" onFocus={onFocus} onBlur={onBlur}
                              className="w-full border rounded-lg px-10 py-2.5 text-sm focus:outline-none transition-colors"
                              style={inputStyle} />
                            {fetchingLogo && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                                  style={{ borderColor: tc.accent }} />
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1.5" style={{ color: tc.muted }}>
                            Self-Destruct / Expiration (TTL)
                          </label>
                          <select
                            value={form.ttl || 'none'}
                            onChange={(e) => {
                              const v = e.target.value;
                              const ms = v === '24h' ? 86400000 : v === '7d' ? 604800000 : v === '30d' ? 2592000000 : null;
                              setForm((p) => ({ ...p, ttl: v, expiresAt: ms ? Date.now() + ms : null }));
                            }}
                            className="w-full border rounded-lg px-3 py-2 text-xs focus:outline-none transition-colors cursor-pointer"
                            style={inputStyle}
                          >
                            <option value="none" className={isDark ? 'bg-neutral-900 text-white' : 'bg-white text-slate-900'}>None (Permanent Bookmark)</option>
                            <option value="24h" className={isDark ? 'bg-neutral-900 text-white' : 'bg-white text-slate-900'}>⏳ Expire in 24 Hours</option>
                            <option value="7d" className={isDark ? 'bg-neutral-900 text-white' : 'bg-white text-slate-900'}>⏳ Expire in 7 Days</option>
                            <option value="30d" className={isDark ? 'bg-neutral-900 text-white' : 'bg-white text-slate-900'}>⏳ Expire in 30 Days</option>
                          </select>
                        </div>

                        {form.logoUrl && (
                          <div className="flex items-center gap-3 p-3 border rounded-lg"
                            style={{ backgroundColor: inputBg, borderColor: tc.border }}>
                            <img src={form.logoUrl} alt="logo" className="w-8 h-8 rounded-lg object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }} />
                            <span className="text-xs" style={{ color: tc.muted }}>Auto-fetched logo</span>
                          </div>
                        )}
                      </motion.div>
                    )}

                    {activeTab === 'note' && (
                      <motion.div key="note" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} className="space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="flex items-center gap-1.5 text-xs font-medium" style={{ color: tc.muted }}>
                            <SafeIcon icon={FiFileText} className="w-3.5 h-3.5" /> Note & Tags
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const suggested = inferSmartTags(form.url, form.title);
                              const existing = form.note ? form.note.trim() : '';
                              const toAdd = suggested.filter((t) => !existing.includes(t)).join(' ');
                              setForm((p) => ({ ...p, note: existing ? `${existing} ${toAdd}` : toAdd }));
                            }}
                            className="text-[11px] font-mono px-2 py-0.5 rounded-lg border flex items-center gap-1 text-purple-300 bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 transition-all cursor-pointer"
                            title="Infer tags based on URL and title"
                          >
                            <span>✨ Auto-Tag</span>
                          </button>
                        </div>
                        <textarea value={form.note} onChange={set('note')} rows={5}
                          placeholder="Add notes or tags (e.g. #dev #reading #ai)..."
                          className="w-full border rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none transition-colors"
                          style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                        
                        <div className="flex items-center flex-wrap gap-1.5 pt-1">
                          <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Quick Tags:</span>
                          {['#dev', '#ai', '#design', '#media', '#finance', '#reading', '#tools'].map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                const existing = form.note || '';
                                if (!existing.includes(tag)) {
                                  setForm((p) => ({ ...p, note: `${existing} ${tag}`.trim() }));
                                }
                              }}
                              className={`text-[10px] px-2 py-0.5 rounded-md font-mono transition-colors cursor-pointer border ${
                                isDark
                                  ? 'bg-white/[0.04] hover:bg-white/[0.08] text-white/70 border-white/10'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                              }`}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    {activeTab === 'credentials' && (
                      <motion.div key="creds" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} className="space-y-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <label className="flex items-center gap-1.5 text-xs font-medium" style={{ color: tc.muted }}>
                              <SafeIcon icon={FiLock} className="w-3.5 h-3.5" /> Credentials
                            </label>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                              <SafeIcon icon={FiShield} className="w-2.5 h-2.5" />
                              AES-256
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                const pwd = generatePassword({ length: 18 });
                                setCredPass(pwd);
                                setShowCreds(true);
                              }}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border transition-all hover:scale-105"
                              style={{ borderColor: `${tc.accent}50`, color: tc.accent, backgroundColor: `${tc.accent}15` }}
                              title="Generate Strong Password"
                            >
                              <SafeIcon icon={FiKey} className="w-3 h-3" /> Gen Pass
                            </button>
                            <button type="button" onClick={() => setShowCreds((v) => !v)}
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg"
                              style={{ color: tc.muted }}>
                              <SafeIcon icon={showCreds ? FiEyeOff : FiEye} className="w-3 h-3" />
                              {showCreds ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </div>

                        {/* Email or Username */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: tc.muted }}>Email / Username</label>
                          <div className="relative">
                            <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: tc.muted }} />
                            <input
                              type="text"
                              value={credUser}
                              onChange={(e) => setCredUser(e.target.value)}
                              placeholder="user@example.com or username"
                              onFocus={onFocus} onBlur={onBlur}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Password */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: tc.muted }}>Password</label>
                          <div className="relative">
                            <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: tc.muted }} />
                            <input
                              type={showCreds ? 'text' : 'password'}
                              value={credPass}
                              onChange={(e) => setCredPass(e.target.value)}
                              placeholder="••••••••••••"
                              onFocus={onFocus} onBlur={onBlur}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Activation Code / 2FA Key */}
                        <div>
                          <label className="block text-[11px] font-medium mb-1" style={{ color: tc.muted }}>Activation Code / License / 2FA</label>
                          <div className="relative">
                            <SafeIcon icon={FiZap} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: tc.muted }} />
                            <input
                              type="text"
                              value={credCode}
                              onChange={(e) => setCredCode(e.target.value)}
                              placeholder="e.g. ABCD-1234-EFGH or 2FA secret"
                              onFocus={onFocus} onBlur={onBlur}
                              className="w-full border rounded-lg pl-9 pr-3 py-2 text-xs font-mono focus:outline-none transition-colors"
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        <p className="text-[10px] pt-1" style={{ color: tc.muted }}>
                          Encrypted on your device before saving. Auto-fill will match this domain.
                        </p>
                      </motion.div>
                    )}

                  </AnimatePresence>
                </div>

                <div className="flex gap-3 px-6 py-4 border-t mt-2" style={{ borderColor: tc.border }}>
                  <button type="button" onClick={onClose}
                    className="px-4 py-2.5 rounded-lg text-sm border transition-colors"
                    style={{ borderColor: tc.border, color: tc.muted }}>
                    Cancel
                  </button>
                  <button type="submit"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
                    style={{ backgroundColor: tc.accent, color: '#000' }}>
                    <SafeIcon icon={FiSave} className="w-4 h-4" /> Add Bookmark
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}